import ModalHeader from '@/components/modal/ModalHeader.tsx';
import React from 'react';
import Modal from '@/components/modal/Modal.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import { Check, Copy, Link } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '@/components/ui/Card.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { tourRoute } from '@/features/dashboard/tours/routes/tourRoute.ts';
import useTourLink from '@/features/dashboard/tours/hooks/useTourLink.ts';
import useActivateTour from '@/features/dashboard/tourConfig/hooks/useActivateTour.ts';

const TourCreatedDialog = ({ onClose }: { onClose: () => void }) => {
  const navigate = useNavigate();
  const { tourId } = tourRoute.useParams();

  const { tourLink, isLoading } = useTourLink(tourId);
  const { mutate: activateTourMutate, isPending: isActivating } =
    useActivateTour();

  const handleCloseModal = () => {
    navigate({
      to: '/app/tours/$tourId',
      params: { tourId },
      search: {},
      replace: true,
    });
    onClose();
  };

  const copyTourLinkToClipboard = () => {
    if (isLoading || !tourLink) return;
    navigator.clipboard.writeText(tourLink);
    toast.success('Link do formularza został skopiowany do schowka');
  };

  const activateTour = () => {
    copyTourLinkToClipboard();
    activateTourMutate(tourId, {
      onSuccess: () => handleCloseModal(),
    });
  };

  return (
    <Modal onClose={handleCloseModal}>
      <ModalHeader
        title="Czy chcesz aktywować wyjazd?"
        onClose={handleCloseModal}
      />
      <ModalContent>
        <div className="flex flex-row gap-2">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-md bg-success-100">
            <Check className="size-6 text-success-300" />
          </div>
          <div className="space-y-2">
            <CardTitle>Wyjazd zapisany</CardTitle>
            <CardDescription>
              Wyjazd zapisany jako wersja robocza. Możesz opublikować formularz
              zapisowy do obozu teraz lub zrobić to w dowolnym momencie z tej
              zakładki.
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <Card>
            <CardContent className="space-y-2">
              <Link className="size-4 text-accent-600" />
              <CardTitle className="text-lg font-medium">
                Wygeneruj link
              </CardTitle>
              <CardDescription>
                Wygeneruj link i samodzielnie wyślij do uczestników. Link jest
                publiczny i każda osoba z linkiem będzie mogła wypełnić
                formularz.
              </CardDescription>
              <ActionButton
                onClick={copyTourLinkToClipboard}
                disabled={isLoading}
                tone="outline"
                size="sm"
              >
                <Copy />
                {isLoading ? 'Ładowanie...' : 'Skopiuj link'}
              </ActionButton>
            </CardContent>
          </Card>
        </div>
      </ModalContent>
      <ModalFooter>
        <ActionButton tone="outline" size="sm" onClick={handleCloseModal}>
          Zamknij
        </ActionButton>
        <ActionButton
          tone="accent"
          size="sm"
          onClick={activateTour}
          disabled={isActivating}
        >
          <Copy />
          {isActivating ? 'Ładowanie...' : 'Aktywuj'}
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default TourCreatedDialog;
